'use client';

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { capture } from '@/lib/analytics/posthog';

/**
 * Fires a `landing_section_viewed` event once when the referenced element
 * scrolls into view (30% visible).
 *
 * Accepts an existing ref so it composes with GSAP animation refs without
 * needing a merged-ref pattern.
 */
export function useTrackSectionView(
  sectionName: string,
  ref: RefObject<HTMLElement | null>,
): void {
  const tracked = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || tracked.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          tracked.current = true;
          capture('landing_section_viewed', { section: sectionName });
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionName, ref]);
}
